import { useState, useEffect } from 'react';
import {
  DialogContent,
  TextField,
  FormControlLabel,
  Switch,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@/components/common/FormDialog';
import { useFormDirty } from '@/hooks/useFormDirty';
import type { HoursException } from '@/types';

interface HoursExceptionDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (exception: HoursException) => void;
  exception?: HoursException | null;
  /** True while the parent persists the exception — blocks a double submit. */
  isSaving?: boolean;
}

export function HoursExceptionDialog({
  open,
  onClose,
  onSave,
  exception,
  isSaving = false,
}: HoursExceptionDialogProps) {
  const { t } = useTranslation('settings');
  const { isDirty, markDirty } = useFormDirty(open);

  const isEditing = !!exception;

  const [date, setDate] = useState('');
  const [isClosed, setIsClosed] = useState(true);
  const [openTime, setOpenTime] = useState('08:00');
  const [closeTime, setCloseTime] = useState('17:00');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      if (exception) {
        setDate(exception.date);
        setIsClosed(exception.isClosed);
        setOpenTime(exception.open ?? '08:00');
        setCloseTime(exception.close ?? '17:00');
        setReason(exception.reason ?? '');
      } else {
        setDate('');
        setIsClosed(true);
        setOpenTime('08:00');
        setCloseTime('17:00');
        setReason('');
      }
    }
  }, [open, exception]);

  const handleSave = () => {
    if (!date) return;

    const saved: HoursException = {
      id: exception?.id ?? crypto.randomUUID(),
      date,
      isClosed,
      ...(!isClosed && { open: openTime, close: closeTime }),
      ...(reason.trim() && { reason: reason.trim() }),
    };
    // Deliberately does NOT close here: the parent persists the exception and
    // closes on success, so a rejected save (e.g. a duplicate date) keeps the
    // dialog open with the user's input instead of discarding it (#1282).
    onSave(saved);
  };

  const isValid = date.length > 0;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEditing ? t('editException') : t('addException')}
      isDirty={isDirty}
      onSubmit={handleSave}
      isLoading={isSaving}
      submitDisabled={!isValid || isSaving}
      maxWidth="xs"
      fullWidth
    >
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <TextField
            fullWidth
            type="date"
            label={t('exceptionDate')}
            value={date}
            onChange={(e) => { setDate(e.target.value); markDirty(); }}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={isClosed}
                onChange={(e) => { setIsClosed(e.target.checked); markDirty(); }}
              />
            }
            label={t('closedAllDay')}
          />

          {!isClosed && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                type="time"
                label={t('opensAt')}
                value={openTime}
                onChange={(e) => { setOpenTime(e.target.value); markDirty(); }}
                sx={{ flex: 1 }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                type="time"
                label={t('closesAt')}
                value={closeTime}
                onChange={(e) => { setCloseTime(e.target.value); markDirty(); }}
                sx={{ flex: 1 }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Box>
          )}

          <TextField
            fullWidth
            label={t('exceptionReason')}
            placeholder={t('exceptionReasonPlaceholder')}
            value={reason}
            onChange={(e) => { setReason(e.target.value); markDirty(); }}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
        </Box>
      </DialogContent>
    </FormDialog>
  );
}
